import { createStyles, Group } from '@mantine/core';
import type { ERDFlowColumn } from '@repo/api-codegen';
import { Badge, Icon, Text } from '@repo/foundations';
import { Handle, Position } from '@xyflow/react';

import { observer } from 'mobx-react-lite';
import { useCallback } from 'react';
import { useERDFlowContext } from '../../context/ERDFlowProvider/ERDFlowContext';
import { ENTITY_NODE_PADDING } from '../EntityNode/EntityNode.constants';
import { useStyles as useEntityNodeChildStyles } from '../EntityNodeChildren/EntityNodeChild.styles';
import { ENTITY_CHILDREN_ROW_HEIGHT } from '../EntityNodeChildren/EntityNodeChildren.constants';

interface TableNodeColumnProps {
	column: ERDFlowColumn;
	order: number;
	focused?: boolean;
	highlighted?: boolean;
	disabled?: boolean;
}

const useStyles = createStyles((theme, { order }: { order: number }) => ({
	handle: {
		visibility: 'hidden',
		top: `${
			ENTITY_NODE_PADDING +
			ENTITY_CHILDREN_ROW_HEIGHT * order +
			ENTITY_CHILDREN_ROW_HEIGHT * 0.5
		}px !important`,
	},
}));

const TableNodeColumn = observer(
	({
		column,
		focused,
		highlighted,
		order,
		disabled = false,
	}: TableNodeColumnProps) => {
		const { onSetActiveColumnId } = useERDFlowContext();

		const { classes: entityNodeChildClasses } = useEntityNodeChildStyles({
			highlighted,
			focused,
			order,
			createAccess: false,
			disabled,
		});
		const { classes } = useStyles({ order });

		const handleSetActiveColumnId = useCallback(() => {
			if (disabled) return;

			onSetActiveColumnId(column.id);
		}, [column.id, onSetActiveColumnId, disabled]);

		return (
			<Group
				className={entityNodeChildClasses.wrapper}
				onClick={handleSetActiveColumnId}
			>
				{!disabled && (
					<Handle
						id={`COLUMN_TARGET--${column.id}`}
						className={classes.handle}
						type="target"
						position={Position.Left}
					/>
				)}

				<Group position="apart" w="100%">
					<Text
						color={disabled ? 'text/secondary/default' : 'text/primary/default'}
					>
						{column.title}
					</Text>
					<Group spacing={'3xs'} noWrap>
						{column.is_pk && (
							<Badge
								variant="info"
								leftSection={<Icon name="key" color="icon/info/default" />}
							>
								PK
							</Badge>
						)}
						{column.is_fk && (
							<Badge
								variant="default"
								leftSection={<Icon name="key" color="icon/secondary/default" />}
							>
								FK
							</Badge>
						)}
					</Group>
				</Group>
				{!disabled && (
					<Handle
						id={`COLUMN_SOURCE--${column.id}`}
						className={classes.handle}
						type="source"
						position={Position.Right}
					/>
				)}
			</Group>
		);
	}
);

export default TableNodeColumn;
